import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

const APP_NAME = 'Niswa Info Tech';
const APP_ICON = '/icon-192.png?v=2';
// Per-session dismissal: the popup re-appears on the next visit/session so it
// greets the user at the start of every visit, on every device.
const SESSION_KEY = 'niswaPwaDismissedSession';

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true ||
    document.referrer.startsWith('android-app://')
  );
}

function isIOS() {
  const ua = window.navigator.userAgent || '';
  const iOSDevice = /iPad|iPhone|iPod/.test(ua);
  const iPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}

function dismissedThisSession() {
  try {
    return sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [iosMode, setIosMode] = useState(false);

  const close = useCallback((remember = true) => {
    setVisible(false);
    if (remember) {
      try {
        sessionStorage.setItem(SESSION_KEY, '1');
      } catch {
        /* ignore */
      }
    }
  }, []);

  useEffect(() => {
    if (isStandalone()) return; // already installed – nothing to do

    const showSoon = (delay) => {
      if (!dismissedThisSession()) setTimeout(() => setVisible(true), delay);
    };

    // The install prompt may have been captured by the early script in
    // index.html before React mounted. If so, use it right away.
    if (window.deferredPWAPrompt) {
      setDeferredPrompt(window.deferredPWAPrompt);
      showSoon(600);
    }

    // In case the event fires after mount.
    const onInstallable = () => {
      if (window.deferredPWAPrompt) {
        setDeferredPrompt(window.deferredPWAPrompt);
        showSoon(400);
      }
    };
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      window.deferredPWAPrompt = e;
      showSoon(400);
    };
    const onInstalled = () => {
      setInstalled(true);
      setVisible(true);
      setDeferredPrompt(null);
      window.deferredPWAPrompt = null;
      setTimeout(() => setVisible(false), 4000);
    };

    window.addEventListener('pwaInstallable', onInstallable);
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    window.addEventListener('pwaInstalled', onInstalled);

    // iOS Safari has no beforeinstallprompt – show manual instructions instead.
    if (isIOS() && !dismissedThisSession()) {
      setIosMode(true);
      showSoon(1500);
    }

    return () => {
      window.removeEventListener('pwaInstallable', onInstallable);
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      window.removeEventListener('pwaInstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      const choice = await deferredPrompt.userChoice;
      if (choice && choice.outcome === 'accepted') {
        setVisible(false);
      } else {
        close(true);
      }
    } catch {
      close(true);
    }
    setDeferredPrompt(null);
  };

  if (!visible) return null;

  const accent = 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 60%, #ec4899 100%)';

  const popup = (
    <>
      <style>{`
        @keyframes niswaPwaFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes niswaPwaPop {
          from { opacity: 0; transform: translateY(20px) scale(.94); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .niswa-pwa-backdrop {
          position: fixed; inset: 0; z-index: 2147483000;
          background: rgba(6, 8, 16, 0.62);
          backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
          display: flex; align-items: center; justify-content: center;
          padding: 20px; animation: niswaPwaFade .25s ease both;
        }
        .niswa-pwa-card {
          position: relative; width: min(420px, 100%);
          background: rgba(17, 19, 30, 0.96);
          color: #fff; border: 1px solid rgba(255,255,255,0.12);
          border-radius: 24px; padding: 26px 22px 22px;
          box-shadow: 0 30px 80px rgba(0,0,0,0.55);
          animation: niswaPwaPop .5s cubic-bezier(.16,1,.3,1) both;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          text-align: center;
        }
        .niswa-pwa-icon {
          width: 84px; height: 84px; border-radius: 20px; display: block; margin: 0 auto 14px;
          box-shadow: 0 10px 30px rgba(99,102,241,.5);
        }
        .niswa-pwa-title { font-size: 20px; font-weight: 800; line-height: 1.2; }
        .niswa-pwa-sub { font-size: 13.5px; color: rgba(255,255,255,0.7); margin-top: 8px; line-height: 1.5; }
        .niswa-pwa-btn {
          width: 100%; border: none; cursor: pointer; font-weight: 800; font-size: 15px;
          color: #fff; padding: 14px 20px; border-radius: 14px; margin-top: 20px;
          background: ${accent}; box-shadow: 0 10px 26px rgba(99,102,241,.45);
          transition: transform .15s ease, box-shadow .15s ease;
        }
        .niswa-pwa-btn:hover { transform: translateY(-1px); box-shadow: 0 14px 32px rgba(99,102,241,.6); }
        .niswa-pwa-btn:active { transform: translateY(0); }
        .niswa-pwa-ghost {
          width: 100%; margin-top: 10px; border: none; background: transparent;
          color: rgba(255,255,255,0.6); padding: 10px; border-radius: 12px; font-weight: 700;
          font-size: 13.5px; cursor: pointer;
        }
        .niswa-pwa-ghost:hover { color: #fff; }
        .niswa-pwa-x {
          position: absolute; top: 14px; right: 14px; width: 30px; height: 30px;
          border-radius: 50%; border: none; cursor: pointer; color: rgba(255,255,255,.7);
          background: rgba(255,255,255,0.08); font-size: 17px; line-height: 1;
          display: flex; align-items: center; justify-content: center;
        }
        .niswa-pwa-x:hover { background: rgba(255,255,255,0.16); color: #fff; }
        .niswa-pwa-steps { margin: 18px 0 0; padding: 0; list-style: none; display: grid; gap: 10px; text-align: left; }
        .niswa-pwa-steps li { font-size: 13.5px; color: rgba(255,255,255,0.84); display: flex; gap: 10px; align-items: center; }
        .niswa-pwa-step-no {
          width: 24px; height: 24px; border-radius: 50%; flex-shrink: 0; font-size: 12px; font-weight: 800;
          display: inline-flex; align-items: center; justify-content: center; color: #fff; background: ${accent};
        }
      `}</style>

      <div
        className="niswa-pwa-backdrop"
        onClick={(e) => {
          if (e.target === e.currentTarget) close(true);
        }}
      >
        <div className="niswa-pwa-card" role="dialog" aria-modal="true" aria-label={`Install ${APP_NAME}`}>
          <button className="niswa-pwa-x" aria-label="Dismiss" onClick={() => close(true)}>
            &times;
          </button>

          <img className="niswa-pwa-icon" src={APP_ICON} alt={APP_NAME} />

          {installed ? (
            <>
              <div className="niswa-pwa-title">App installed</div>
              <div className="niswa-pwa-sub">{APP_NAME} has been added to your device.</div>
            </>
          ) : iosMode ? (
            <>
              <div className="niswa-pwa-title">Install {APP_NAME} App</div>
              <div className="niswa-pwa-sub">Add our app to your Home Screen for a faster, full-screen experience.</div>
              <ul className="niswa-pwa-steps">
                <li>
                  <span className="niswa-pwa-step-no">1</span>
                  Tap the <strong>&nbsp;Share&nbsp;</strong> button in Safari&apos;s toolbar.
                </li>
                <li>
                  <span className="niswa-pwa-step-no">2</span>
                  Choose <strong>&nbsp;Add to Home Screen&nbsp;</strong>.
                </li>
                <li>
                  <span className="niswa-pwa-step-no">3</span>
                  Tap <strong>&nbsp;Add&nbsp;</strong> — done!
                </li>
              </ul>
              <button className="niswa-pwa-ghost" onClick={() => close(true)}>
                Got it
              </button>
            </>
          ) : (
            <>
              <div className="niswa-pwa-title">Install {APP_NAME} App</div>
              <div className="niswa-pwa-sub">
                Install our app on your device for quick access, offline support &amp; a full-screen experience.
              </div>
              <button className="niswa-pwa-btn" onClick={handleInstall}>
                Install App
              </button>
              <button className="niswa-pwa-ghost" onClick={() => close(true)}>
                Maybe later
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );

  return createPortal(popup, document.body);
};

export default InstallPWA;
